import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * ROC SIGNAL — componentes base del sistema visual (Fase A).
 * Editorial + metadata técnica. Sin tarjetas, sin iconos decorativos.
 * Todos los textos se renderizan en el HTML inicial: la motion nunca
 * es requisito para leer el contenido.
 */

/** Etiqueta de metadata técnica: `CASE / 001`, `HOUSTON → ESTADOS UNIDOS`. */
export const SignalMeta = ({
  children,
  className,
  tone = "muted",
}: {
  children: ReactNode;
  className?: string;
  tone?: "muted" | "signal" | "ink";
}) => (
  <span
    className={cn(
      "roc-meta inline-flex items-center gap-2",
      tone === "signal" && "text-signal",
      tone === "muted" && "text-stone",
      tone === "ink" && "text-ink",
      className,
    )}
  >
    {children}
  </span>
);

/** Regla de medición con marca de señal al inicio. */
export const SignalRule = ({
  className,
  marked = true,
}: {
  className?: string;
  marked?: boolean;
}) => (
  <div aria-hidden="true" className={cn("relative flex items-center", className)}>
    {marked && <span className="mr-3 h-[7px] w-[7px] shrink-0 bg-signal" />}
    <span className="roc-rule" />
  </div>
);

/**
 * Cabecera de sección editorial: número de índice, metadata y titular grande.
 * Recibe el nivel semántico para no alterar la jerarquía SEO existente.
 */
export const SectionOpener = ({
  index,
  meta,
  title,
  lead,
  as: Tag = "h2",
  tone = "light",
  className,
}: {
  index: string;
  meta: string;
  title: ReactNode;
  lead?: ReactNode;
  as?: "h1" | "h2" | "h3";
  tone?: "light" | "dark";
  className?: string;
}) => (
  <div className={cn("roc-grid12 items-end gap-y-8", className)}>
    <div className="col-span-12 lg:col-span-3">
      <SignalMeta tone={tone === "dark" ? "signal" : "signal"}>{index}</SignalMeta>
      <p
        className={cn(
          "roc-meta mt-3",
          tone === "dark" ? "text-stone-soft" : "text-stone",
        )}
      >
        {meta}
      </p>
      <SignalRule className={cn("mt-5 max-w-[10rem]", tone === "dark" ? "text-stone-soft" : "text-ink")} />
    </div>
    <div className="col-span-12 lg:col-span-9">
      <Tag
        className={cn(
          "font-display text-balance text-[2rem] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[2.75rem] lg:text-[3.5rem]",
          tone === "dark" ? "text-paper" : "text-ink",
        )}
      >
        {title}
      </Tag>
      {lead && (
        <p
          className={cn(
            "mt-6 max-w-2xl text-[1.0625rem] leading-relaxed lg:text-lg",
            tone === "dark" ? "text-stone-soft" : "text-ink/70",
          )}
        >
          {lead}
        </p>
      )}
    </div>
  </div>
);

/** Bloque de dato: cifra grande + etiqueta + nota de procedencia. */
export const SignalFigure = ({
  value,
  label,
  note,
  tone = "dark",
  className,
}: {
  value: ReactNode;
  label: ReactNode;
  note?: ReactNode;
  tone?: "light" | "dark";
  className?: string;
}) => (
  <div className={cn("flex flex-col", className)}>
    <span
      className={cn(
        "roc-figure text-[2rem] font-semibold leading-none sm:text-[2.5rem]",
        tone === "dark" ? "text-paper" : "text-ink",
      )}
    >
      {value}
    </span>
    <span className={cn("roc-meta mt-3", tone === "dark" ? "text-stone-soft" : "text-stone")}>{label}</span>
    {note && (
      <span className={cn("mt-2 max-w-[22ch] text-xs leading-relaxed", tone === "dark" ? "text-stone" : "text-ink/55")}>
        {note}
      </span>
    )}
  </div>
);

/** Fondo de coordenadas + grano. Decorativo, nunca portador de información. */
export const SignalField = ({
  tone = "dark",
  className,
}: {
  tone?: "light" | "dark";
  className?: string;
}) => (
  <>
    <div
      aria-hidden="true"
      className={cn(
        "roc-coords pointer-events-none absolute inset-0",
        tone === "dark" ? "text-paper" : "text-ink",
        className,
      )}
    />
    <div aria-hidden="true" className="roc-grain pointer-events-none absolute inset-0" />
  </>
);
